import { AxiosError } from "axios";
import { isEqual } from "lodash-es";
import { ChangeEvent, FC, useRef } from "react";
import {
  type FieldPath,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";

import { serverImportExternalSection } from "api/api-server-actions";
import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import { RubricType, SectionScoreDisplayFormat } from "components/server-types";
import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { FormField } from "mds/components/FormField";
import { Listbox } from "mds/components/Listbox";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleInfoIcon, ScoreIcon } from "mds/icons";
import { storeApi } from "store/index";
import { selectCurrentOrg } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import {
  toastLocalizedOperationCatcher,
  toastLocalizedOperationError,
  toastSuccessMessage,
} from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

type AdminSettingsFormProps = {
  readOnly?: boolean;
};

type FormValues = {
  name: string;
  ai_assessment_enabled: boolean;
  rubric: RubricType;
  section_score_display_format: SectionScoreDisplayFormat;
};

const getSectionScoreDisplayFormatDropdownValues = () => {
  return Object.values(SectionScoreDisplayFormat).map((value) => ({
    id: value,
    title: t(`home.settings.section_score_display.options.${value}`),
  }));
};

const toastSaveError = (rubricFailure: boolean) => {
  toastLocalizedOperationError(
    rubricFailure ? "failed_to_update_rubric" : "failed_to_update_institution_settings",
  );
};

const onImportSection = async (event: ChangeEvent<HTMLInputElement>) => {
  event.stopPropagation();
  event.preventDefault();

  const file = event.target.files![0];

  const formData = new FormData();
  formData.append("data", file);

  const response = await serverImportExternalSection(formData);

  if (response) {
    toastSuccessMessage(t("home.settings.import_section_success"));
    trackEvent("Course imported", {
      ...GENERAL_EVENT_CATEGORY,
      course: response.course.code,
      courseTerm: response.term.title,
      sectionTitle: response.title,
    });
  }
};

export const AdminSettingsForm: FC<AdminSettingsFormProps> = ({ readOnly = false }) => {
  const currentOrg = useObjectSelector(selectCurrentOrg, [
    "id",
    "name",
    "ai_assessment_enabled",
    "rubric",
    "section_score_display_format",
  ]);
  const sectionInput = useRef<HTMLInputElement>(null);

  const form = useForm<FormValues>({
    defaultValues: {
      name: currentOrg?.name ?? "",
      ai_assessment_enabled: currentOrg?.ai_assessment_enabled ?? false,
      rubric: currentOrg?.rubric ?? [],
      section_score_display_format:
        currentOrg?.section_score_display_format || SectionScoreDisplayFormat.Percent,
    },
  });

  const { fields: rubricFields } = useFieldArray({
    control: form.control,
    name: "rubric",
  });

  if (!currentOrg) {
    return null;
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    // Snapshot before partial_update's optimistic store write so change tracking and
    // error toasts still reflect the pre-save org (not the in-flight form values).
    const orgSnapshot = currentOrg;
    const aiAssessmentChanged =
      formData.ai_assessment_enabled !== orgSnapshot.ai_assessment_enabled;
    const rubricChanged = !isEqual(formData.rubric, orgSnapshot.rubric);
    const displayFormatChanged =
      formData.section_score_display_format !== orgSnapshot.section_score_display_format;

    storeApi.orgs
      .partial_update({
        id: orgSnapshot.id,
        name: formData.name,
        ai_assessment_enabled: formData.ai_assessment_enabled,
        rubric: formData.rubric,
        section_score_display_format: formData.section_score_display_format,
      })
      .then(() => {
        toastSuccessMessage(t("success.toasts.updated_institution_settings"));
        if (aiAssessmentChanged) {
          trackEvent("Org Settings - AI assessment toggle", {
            ...BUTTON_EVENT_CATEGORY,
            isEnabled: formData.ai_assessment_enabled,
          });
        }
        if (rubricChanged) {
          trackEvent("Org Settings - Save New Rubric", BUTTON_EVENT_CATEGORY);
        }
        if (displayFormatChanged) {
          trackEvent("Org Settings - Save New Course Score Display Format", BUTTON_EVENT_CATEGORY);
        }
      })
      .catch((error: unknown) => {
        if (!(error instanceof AxiosError)) {
          toastSaveError(rubricChanged);
          return;
        }
        if (error.response?.status !== 400) {
          toastSaveError(rubricChanged);
          return;
        }
        const respData = error.response?.data as {
          input: string;
          loc: [] | ["levels", number, "name" | "score" | "conversion_percent"];
          msg: string;
          type: string;
          url?: string;
        }[];
        if (!Array.isArray(respData)) {
          toastSaveError(rubricChanged);
          return;
        }
        respData.forEach(({ loc, msg }) => {
          if (loc.length === 0) {
            form.setError("root", {
              type: "server",
              message: msg,
            });
          } else if (loc[0] === "levels") {
            form.setError(`rubric.${loc[1]}.${loc[2]}`, {
              type: "server",
              message: msg,
            });
          }
        });
      });
  };

  const sectionScoreDisplayFormat = form.watch("section_score_display_format");
  const { isDirty } = form.formState;

  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col gap-3"
        onSubmit={!readOnly ? form.handleSubmit(onSubmit) : undefined}
      >
        <FormField
          label={t("home.settings.institution_name")}
          name="name"
          readOnly={readOnly}
          required
        >
          {(controlProps) => (
            <div className="w-[600px] max-w-full">
              <TextField
                {...form.register("name", {
                  required: t("error.field_required"),
                })}
                {...controlProps}
              />
            </div>
          )}
        </FormField>

        <Checkbox {...form.register("ai_assessment_enabled", { disabled: readOnly })}>
          {t("home.settings.ai_assessment_enabled")}
        </Checkbox>

        <div className="flex items-center">
          <SquareButton
            disabled={readOnly}
            eventName="Org Settings - Import course"
            kind="tertiary"
            size="s"
            type="button"
            onClick={() => sectionInput.current?.click()}
          >
            {t("home.settings.import_section")}
          </SquareButton>

          <input
            className="invisible w-0"
            disabled={readOnly}
            ref={sectionInput}
            type="file"
            onChange={(event) => {
              onImportSection(event).catch(
                toastLocalizedOperationCatcher("import_section_failure"),
              );
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="body-s text-black-tint-20">
            {t("home.settings.rubric_conversion_scale.title")}
          </div>
          <div className="body-xs text-black-tint-55">
            {t("home.settings.rubric_conversion_scale.description")}
          </div>
          <div className="body-xs flex flex-row items-center gap-2 text-black-tint-55">
            <CircleInfoIcon className="text-blue" />
            <span>{t("home.settings.rubric_conversion_scale.change_alert")}</span>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <ErrorMessageBanner name="root" />
          {rubricFields.map((field, index) => {
            // The first and last levels of the rubric have immutable conversion_percent values of 0% and 100%,
            // respectively. Interior levels have a conversion_percent value that may be edited.
            const levelDisabled = [0, rubricFields.length - 1].includes(index);
            return (
              <div className="flex flex-row items-start gap-2" key={field.id}>
                <ScoreIcon className="mt-0.5" value={field.score} />
                <FormField
                  disabled={levelDisabled}
                  label={field.name}
                  name={`rubric.${index}.conversion_percent`}
                  readOnly={readOnly}
                  required
                >
                  {(controlProps) => (
                    <div className="w-[72px]">
                      <TextField
                        {...form.register(`rubric.${index}.conversion_percent`, {
                          // Keep disabled off register so first/last levels still submit (0% / 100%).
                          setValueAs: (value: string | number): number | null => {
                            if (typeof value === "string") {
                              return value.trim() === "" ? null : +value;
                            }
                            return value;
                          },
                          // Re-validate neighbors when this value changes so order errors clear/update together.
                          deps: [
                            ...(index > 0
                              ? [`rubric.${index - 1}.conversion_percent` as FieldPath<FormValues>]
                              : []),
                            ...(index < rubricFields.length - 1
                              ? [`rubric.${index + 1}.conversion_percent` as FieldPath<FormValues>]
                              : []),
                          ],
                          validate: (value: number | null, formValues) => {
                            if (value === null || Number.isNaN(value)) {
                              return t("error.field_required");
                            }
                            if (!Number.isInteger(value) || value < 0 || value > 100) {
                              return t("home.settings.rubric_conversion_scale.percentage_invalid");
                            }
                            // conversion_percent must be non-decreasing down the scale.
                            const previous = formValues.rubric[index - 1]?.conversion_percent;
                            if (
                              typeof previous === "number" &&
                              !Number.isNaN(previous) &&
                              previous > value
                            ) {
                              return t(
                                "home.settings.rubric_conversion_scale.values_must_increase",
                              );
                            }
                          },
                        })}
                        {...controlProps}
                        className="text-right"
                        iconEnd="%"
                      />
                    </div>
                  )}
                </FormField>
              </div>
            );
          })}
        </div>

        <FormField
          helper={t("home.settings.section_score_display.description")}
          label={t("home.settings.section_score_display.title")}
          name="section_score_display_format"
          readOnly={readOnly}
          required
        >
          {(controlProps) => (
            <Listbox
              {...controlProps}
              eventName="Org Settings - Section score display format selection"
              items={getSectionScoreDisplayFormatDropdownValues()}
              selectedValue={sectionScoreDisplayFormat}
              onSelectionChange={({ id }) =>
                form.setValue("section_score_display_format", id, { shouldDirty: true })
              }
            />
          )}
        </FormField>

        <SquareButton
          className="my-8"
          disabled={readOnly || !isDirty}
          kind="accent"
          size="m"
          type="submit"
        >
          {t("common.save")}
        </SquareButton>
      </form>
    </FormProvider>
  );
};
